'use client';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import dynamic from 'next/dynamic';
import { Banner, type BannerProps } from 'components/landing';
import { useModal } from 'components/shared/Modal';
import { PlayWhite } from 'assets/icons';
import { useWindowSize } from 'hooks/dom/useWindowSize';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './VideoBannerWithPlayButton.module.css';

// Defer heavy video player from initial bundle
const DynamicVideoJS = dynamic(() => import('components/heroes/VideoJS'), {
  ssr: false,
});

type ResponsiveSource = string | { mobile: string; web: string };

export interface PlayButtonVideoConfig {
  src: ResponsiveSource;
  poster?: ResponsiveSource;
}

export interface VideoBannerWithPlayButtonProps extends BannerProps {
  playButtonVideoConfig: PlayButtonVideoConfig;
}

const VideoModalPlayer: React.FC<{
  videoSrc: string;
  posterSrc?: string;
  onMount?: () => void;
  onUnmount?: () => void;
}> = ({ videoSrc, posterSrc, onMount, onUnmount }) => {
  useEffect(() => {
    if (onMount) onMount();
    return () => {
      if (onUnmount) onUnmount();
    };
  }, [onMount, onUnmount]);

  const videoOptions = useMemo(() => {
    if (!videoSrc) return undefined as any;
    return {
      autoplay: true,
      controls: true,
      responsive: true,
      fluid: true,
      sources: [{ src: videoSrc, type: 'video/mp4' }],
      playsinline: true,
      preload: 'auto',
      muted: false,
      loop: false,
      poster: posterSrc,
      html5: {
        vhs: { overrideNative: true },
        nativeAudioTracks: false,
        nativeVideoTracks: false,
      },
    } as const;
  }, [videoSrc, posterSrc]);

  if (!videoOptions) return null;

  return (
    <div className={styles.videoModalContent}>
      <DynamicVideoJS key={videoSrc} options={videoOptions} />
    </div>
  );
};

const VideoBannerWithPlayButton: React.FC<VideoBannerWithPlayButtonProps> = ({
  playButtonVideoConfig,
  background,
  ...bannerProps
}) => {
  const modalContext = useModal();
  const { width } = useWindowSize();
  const bannerWrapperRef = useRef<HTMLDivElement>(null);
  const bannerVideoRef = useRef<HTMLVideoElement | null>(null);
  const isModalOpenRef = useRef(false);
  const bodyScrollPositionRef = useRef(0);
  const originalHideRef = useRef<(() => void) | null>(null);

  // Find banner video element
  useEffect(() => {
    const findVideo = () => {
      if (bannerWrapperRef.current && background?.type === 'video') {
        const videoElement = bannerWrapperRef.current.querySelector('video');
        if (videoElement) {
          bannerVideoRef.current = videoElement;
        }
      }
    };

    findVideo();
    // Also check after a delay to catch async rendering
    const timeout = setTimeout(findVideo, 500);
    return () => clearTimeout(timeout);
  }, [background]);

  // Function to pause banner video and prevent scroll
  const handleModalOpen = useCallback(() => {
    if (isModalOpenRef.current) return;
    isModalOpenRef.current = true;

    // Pause banner video
    if (bannerVideoRef.current && !bannerVideoRef.current.paused) {
      bannerVideoRef.current.pause();
    }

    // Prevent body scroll
    bodyScrollPositionRef.current =
      window.pageYOffset || document.documentElement.scrollTop;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${bodyScrollPositionRef.current}px`;
    document.body.style.width = '100%';
  }, []);

  // Function to resume banner video and restore scroll
  const handleModalClose = useCallback(() => {
    if (!isModalOpenRef.current) return;
    isModalOpenRef.current = false;

    // Restore body scroll
    const scrollY = bodyScrollPositionRef.current;
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);

    // Resume banner video if it was paused
    if (bannerVideoRef.current && bannerVideoRef.current.paused) {
      // Check if video is in viewport before resuming
      const rect = bannerVideoRef.current.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;

      if (isInViewport) {
        const playPromise = bannerVideoRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            // Silently handle play promise rejection
          });
        }
      }
    }
  }, []);

  // Wrap modal hide to detect close
  useEffect(() => {
    if (!modalContext) return;

    originalHideRef.current = modalContext.hide;
    const wrappedHide = () => {
      handleModalClose();
      if (originalHideRef.current) {
        originalHideRef.current();
      }
    };
    (modalContext as any).hide = wrappedHide;

    return () => {
      if (originalHideRef.current) {
        (modalContext as any).hide = originalHideRef.current;
      }
    };
  }, [modalContext, handleModalClose]);

  const selectSrc = useCallback(
    (input: ResponsiveSource): string => {
      if (typeof input === 'string') return input;
      if (!width) return input.web;
      return width < 768 ? input.mobile : input.web;
    },
    [width],
  );

  const resolveVideoSrc = useCallback((input: string): string => {
    const isAbsolute = /^(https?:)?\/\//.test(input);
    if (isAbsolute) return input;
    const normalized = input.startsWith('/') ? input : `/${input}`;
    return getCompressedAssetUrl(normalized);
  }, []);

  const resolveImageSrc = useCallback((input: string): string => {
    const isAbsolute = /^(https?:)?\/\//.test(input);
    if (isAbsolute) return input;
    const normalized = input.startsWith('/') ? input : `/${input}`;
    return getCompressedAssetUrl(normalized);
  }, []);

  const resolvedVideoSrc = useMemo(() => {
    if (!playButtonVideoConfig) return '';
    const videoSrc = selectSrc(playButtonVideoConfig.src);
    return resolveVideoSrc(videoSrc);
  }, [playButtonVideoConfig, selectSrc, resolveVideoSrc]);

  const resolvedPosterSrc = useMemo(() => {
    if (!playButtonVideoConfig?.poster) return undefined;
    const posterSrc = selectSrc(playButtonVideoConfig.poster);
    return resolveImageSrc(posterSrc);
  }, [playButtonVideoConfig, selectSrc, resolveImageSrc]);

  const handlePlayButtonClick = useCallback(() => {
    // Immediately pause banner video and prevent scroll
    handleModalOpen();

    const modalContent = (
      <VideoModalPlayer
        videoSrc={resolvedVideoSrc}
        posterSrc={resolvedPosterSrc}
        onMount={handleModalOpen}
        onUnmount={handleModalClose}
      />
    );

    modalContext.set(modalContent);
    modalContext.setPortal(true);
    modalContext.show();
  }, [
    resolvedVideoSrc,
    resolvedPosterSrc,
    modalContext,
    handleModalOpen,
    handleModalClose,
  ]);

  const shouldShowPlayButton =
    background?.type === 'video' && playButtonVideoConfig;

  return (
    <div className={styles.bannerWrapper} ref={bannerWrapperRef}>
      <Banner background={background} {...bannerProps} />
      {shouldShowPlayButton && (
        <button
          className={styles.playButton}
          onClick={handlePlayButtonClick}
          aria-label="Play video with sound"
          type="button"
        >
          <PlayWhite />
        </button>
      )}
    </div>
  );
};

export default VideoBannerWithPlayButton;
